import { useCallback, useEffect, useRef, useState } from 'react';
import { chapters } from '../data';
import { useReducedMotion } from '../motion';
import { closestStoryId, type StoryId } from '../story';
import { PhoneStage } from './PhoneStage';

const MOBILE_QUERY = '(max-width: 899px)';

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia(MOBILE_QUERY).matches
      : false,
  );

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mq = window.matchMedia(MOBILE_QUERY);
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);

  return isMobile;
}

/** Desktop: a sticky phone that swaps screens as the reader scrolls the chapters. */
function DesktopStory() {
  const [active, setActive] = useState<StoryId>('plan');
  const activeRef = useRef<StoryId>('plan');
  const reducedMotion = useReducedMotion();

  useEffect(() => {
    const ratios = new Map<string, number>();
    const nodes = chapters
      .map(({ id }) => document.getElementById(`story-${id}`))
      .filter((node): node is HTMLElement => Boolean(node));

    if (!('IntersectionObserver' in window)) return;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) ratios.set(entry.target.id.replace('story-', ''), entry.intersectionRatio);
        const next = closestStoryId(
          Array.from(ratios, ([id, ratio]) => ({ id, ratio })),
          activeRef.current,
        );
        if (next !== activeRef.current) {
          activeRef.current = next;
          setActive(next);
        }
      },
      { rootMargin: '-25% 0px -35%', threshold: [0, 0.2, 0.4, 0.6, 0.8, 1] },
    );

    nodes.forEach((node) => observer.observe(node));
    return () => observer.disconnect();
  }, []);

  return (
    <div className="story-layout layout-bound">
      <div className="story-stage-desktop">
        <PhoneStage active={active} />
        <nav className="scene-dots" aria-label="Product preview scenes">
          {chapters.map((chapter) => (
            <button
              key={chapter.id}
              type="button"
              className={active === chapter.id ? 'is-active' : ''}
              aria-label={`Show ${chapter.id} preview`}
              aria-pressed={active === chapter.id}
              onClick={() => {
                document.getElementById(`story-${chapter.id}`)?.scrollIntoView({
                  behavior: reducedMotion ? 'auto' : 'smooth',
                  block: 'center',
                });
              }}
            />
          ))}
        </nav>
      </div>
      <div className="story-copy">
        {chapters.map((chapter) => (
          <article
            className={`story-chapter ${active === chapter.id ? 'is-active' : ''}`}
            id={`story-${chapter.id}`}
            key={chapter.id}
            data-story-id={chapter.id}
          >
            <div className="chapter-rule"><span>{chapter.number}</span><span>{chapter.id.toUpperCase()}</span></div>
            <h3>{chapter.title}</h3>
            <p>{chapter.body}</p>
            <ul>
              {chapter.facts.map((fact) => <li key={fact}>{fact}</li>)}
            </ul>
          </article>
        ))}
      </div>
    </div>
  );
}

/**
 * Mobile: the section pins to the viewport and scrolling down translates the
 * banner sideways through each screen, then releases to continue down. Each
 * slide is sized to fit one screen.
 */
function MobileStory() {
  const reducedMotion = useReducedMotion();
  const pinRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const [index, setIndex] = useState(0);
  const count = chapters.length;

  useEffect(() => {
    const outer = pinRef.current;
    const track = trackRef.current;
    if (!outer || !track) return;

    let raf = 0;
    const apply = () => {
      raf = 0;
      const runway = outer.offsetHeight - window.innerHeight;
      const scrolled = Math.min(Math.max(-outer.getBoundingClientRect().top, 0), Math.max(runway, 1));
      const progress = runway > 0 ? scrolled / runway : 0;
      const maxX = track.scrollWidth - track.clientWidth;
      track.style.transform = `translate3d(${-(progress * maxX).toFixed(2)}px, 0, 0)`;
      const next = Math.round(progress * (count - 1));
      setIndex((prev) => (prev === next ? prev : next));
    };
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(apply);
    };

    apply();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [count]);

  const goTo = useCallback(
    (i: number) => {
      const outer = pinRef.current;
      if (!outer || count < 2) return;
      const runway = outer.offsetHeight - window.innerHeight;
      const absoluteTop = window.scrollY + outer.getBoundingClientRect().top;
      window.scrollTo({
        top: absoluteTop + (i / (count - 1)) * runway,
        behavior: reducedMotion ? 'auto' : 'smooth',
      });
    },
    [count, reducedMotion],
  );

  return (
    <div className="story-pin" ref={pinRef} style={{ '--slides': count } as React.CSSProperties}>
      <div className="story-pin-sticky">
        <div className="story-track" ref={trackRef}>
          {chapters.map((chapter) => (
            <article className="story-slide" key={chapter.id} data-story-id={chapter.id}>
              <div className="story-slide-inner">
                <div className="story-stage-mobile">
                  <PhoneStage active={chapter.id} compact />
                </div>
                <div className="chapter-rule"><span>{chapter.number}</span><span>{chapter.id.toUpperCase()}</span></div>
                <h3>{chapter.title}</h3>
                <p>{chapter.body}</p>
              </div>
            </article>
          ))}
        </div>
        <nav className="story-dots-mobile" aria-label="Product screens">
          {chapters.map((chapter, i) => (
            <button
              key={chapter.id}
              type="button"
              className={index === i ? 'is-active' : ''}
              aria-label={`Show ${chapter.id} screen`}
              aria-pressed={index === i}
              onClick={() => goTo(i)}
            />
          ))}
        </nav>
        <p className="story-carousel-note">Scroll · illustrative scenarios; names and data do not represent actual Sticket users or attendance.</p>
      </div>
    </div>
  );
}

export function ProductStory() {
  const isMobile = useIsMobile();

  return (
    <section className="product-story" id="product" aria-labelledby="product-title">
      <div className="section-kicker layout-bound">
        <span>THE PRODUCT STORY</span>
        <span>{isMobile ? 'SCROLL TO MOVE SIDEWAYS' : 'SCROLL NATURALLY · NO SCROLL JACKING'}</span>
      </div>
      <div className="story-heading layout-bound">
        <h2 id="product-title">One night.<br />A life around it.</h2>
        <p>Five views of the same idea: concerts should become more valuable after the lights come up.</p>
      </div>
      {isMobile ? <MobileStory /> : <DesktopStory />}
    </section>
  );
}
